import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  AlertCircle,
  Mail,
  Phone,
  Calendar,
  Shield,
  Building,
  Globe,
} from "lucide-react";
import {
  User,
  UserFormData,
  getRoleIcon,
  getRoleColor,
  getRoleBg,
  formatDate,
  getStatusIcon,
  getStatusColor,
  getStatusBg,
} from "./types";
import { ApiService as api } from "../../services/api";
import { useLanguage } from "../../contexts/LanguageContext";

// AlterCPA removed for Serbia-CC

interface UsersTabProps {
  users: User[];
  accounts: any[]; // Empty array - AlterCPA removed
  loading: boolean;
  error: string | null;
  filters: any;
  onRefresh: () => void;
  onFilterChange: (filters: any) => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (userId: string) => void;
  onToggleUserStatus: (userId: string) => void;
}

// Country mapping: Backend codes to display names
const COUNTRY_MAP: Record<string, string> = {
  ALL: "All Countries",
  MK: "The Republic of Macedonia",
  BG: "Bulgaria",
  RS: "Serbia",
  GR: "Greece",
  TR: "Turkey",
  XK: "Kosovo",
  BA: "Bosnia and Herzegovina",
  SI: "Slovenia",
  HR: "Croatia",
  ME: "Montenegro",
};

const COUNTRY_CODE_TO_NAME: Record<string, string> = {
  Albania: "AL",
  "Bosnia and Herzegovina": "BA",
  Bulgaria: "BG",
  Croatia: "HR",
  Greece: "GR",
  Kosovo: "XK",
  Montenegro: "ME",
  "The Republic of Macedonia": "MK",
  Serbia: "RS",
  Slovenia: "SI",
  Turkey: "TR",
};

export const UsersTab: React.FC<UsersTabProps> = ({
  users,
  accounts,
  loading,
  error,
  filters,
  onRefresh,
  onFilterChange,
  onEditUser,
  onDeleteUser,
  onToggleUserStatus,
}) => {
  const { t } = useLanguage();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // AlterCPA removed for Serbia-CC
  // const [linkedAccountIds, setLinkedAccountIds] = useState<number[]>([]);

  const [formData, setFormData] = useState<UserFormData>({
    username: "",
    email: "",
    password: "",
    role: "",
    countries: [],
    permissions: [],
    profile: {
      firstName: "",
      lastName: "",
      phone: "",
      department: "",
    },
  });

  const [editFormData, setEditFormData] = useState({
    username: "",
    email: "",
    role: "",
    countries: [] as string[],
    isActive: true,
    permissions: [] as string[],
    achievements: [] as string[], // Task achievements for call operators
    profile: {
      firstName: "",
      lastName: "",
      phone: "",
      department: "",
    },
  });

  const availableRoles = [
    "admin",
    "manager",
    "country-manager",
    "country-post-manager",
    "call-center",
    "call-operator",
    "post-operator",
    "website-post-manager",
  ];

  const availableCountries = [
    { code: "MK", name: "The Republic of Macedonia" },
    { code: "BG", name: "Bulgaria" },
    { code: "RS", name: "Serbia" },
    { code: "GR", name: "Greece" },
    { code: "XK", name: "Kosovo" },
    { code: "BA", name: "Bosnia and Herzegovina" },
    { code: "SI", name: "Slovenia" },
    { code: "HR", name: "Croatia" },
    { code: "ME", name: "Montenegro" },
    { code: "TR", name: "Turkey" },
  ];

  // Available permissions grouped by category
  const availablePermissions = [
    {
      category: "Dashboard",
      permissions: [
        { id: "dashboard.view", name: "View Dashboard" },
        { id: "dashboard.edit", name: "Edit Dashboard" },
      ],
    },
    {
      category: "Orders",
      permissions: [
        { id: "orders.view", name: "View Orders" },
        { id: "orders.edit", name: "Edit Orders" },
        { id: "orders.create", name: "Create Orders" },
        { id: "orders.delete", name: "Delete Orders" },
      ],
    },
    {
      category: "Shipping",
      permissions: [
        { id: "shipping.view", name: "View Shipping" },
        { id: "shipping.edit", name: "Edit Shipping" },
        { id: "shipping.create", name: "Create Shipping" },
        { id: "shipping.delete", name: "Delete Shipping" },
      ],
    },
    {
      category: "Analytics",
      permissions: [
        { id: "analytics.view", name: "View Analytics" },
        { id: "analytics.export", name: "Export Analytics" },
      ],
    },
    {
      category: "Call Center",
      permissions: [
        { id: "call-center.view", name: "View Call Center" },
        { id: "call-center.leads.view", name: "View Leads" },
        { id: "call-center.leads.take", name: "Take Leads" },
        { id: "call-center.leads.edit", name: "Edit Leads" },
        { id: "call-center.leads.manage", name: "Manage All Leads" },
        { id: "call-center.orders.view", name: "View Orders" },
        { id: "call-center.orders.edit", name: "Edit Orders" },
        { id: "call-center.orders.create", name: "Create Orders" },
        { id: "call-center.orders.delete", name: "Delete Orders" },
      ],
    },
    {
      category: "Leads",
      permissions: [
        { id: "leads.view", name: "View Leads" },
        { id: "leads.create", name: "Create Leads" },
        { id: "leads.edit", name: "Edit Leads" },
        { id: "leads.delete", name: "Delete Leads" },
      ],
    },
    {
      category: "Users",
      permissions: [
        { id: "users.view", name: "View Users" },
        { id: "users.create", name: "Create Users" },
        { id: "users.edit", name: "Edit Users" },
        { id: "users.delete", name: "Delete Users" },
      ],
    },
    {
      category: "Profile",
      permissions: [
        { id: "profile.view", name: "View Profile" },
        { id: "profile.edit", name: "Edit Profile" },
      ],
    },
    {
      category: "Reports",
      permissions: [
        { id: "reports.view", name: "View Reports" },
        { id: "reports.create", name: "Create Reports" },
        { id: "reports.export", name: "Export Reports" },
      ],
    },
  ];

  // AlterCPA removed for Serbia-CC - no need to load linked accounts
  // const loadLinkedAccounts = async () => { ... }
  // const getAvailableAccounts = (currentLinkedId?: number) => { ... }
  // const getAccountDetails = (accountId?: number) => { ... }

  const handleCreateUser = async () => {
    setCreating(true);
    setFormError(null);
    try {
      // Validate required fields
      if (
        !formData.username ||
        !formData.email ||
        !formData.password ||
        !formData.role
      ) {
        setFormError(t("users.formErrorRequired"));
        setCreating(false);
        return;
      }

      if (!formData.profile.firstName || !formData.profile.lastName) {
        setFormError(t("users.formErrorNameRequired"));
        setCreating(false);
        return;
      }

      // Auto-configure for website-post-manager role
      const isWebsitePostManager = formData.role === "website-post-manager";
      const countries = isWebsitePostManager ? ["GR"] : formData.countries;
      const permissions = isWebsitePostManager 
        ? ["shipping.view", "shipping.edit", "shipping.create", "shipping.delete"] 
        : formData.permissions;

      // Validate countries and permissions for non-website-post-manager roles
      if (!isWebsitePostManager) {
        if (formData.countries.length === 0) {
          setFormError(t("users.formErrorCountryRequired"));
          setCreating(false);
          return;
        }

        if (formData.permissions.length === 0) {
          setFormError(t("users.formErrorPermissionRequired"));
          setCreating(false);
          return;
        }
      }

      // Prepare user data
      const userData = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        role: formData.role,
        countries: countries,
        permissions: permissions,
        // linkedAccountId removed for Serbia-CC
        profile: {
          firstName: formData.profile.firstName.trim(),
          lastName: formData.profile.lastName.trim(),
          phone: formData.profile.phone.trim(),
          department: formData.profile.department.trim(),
        },
      };

      const response = await api.createUser(userData);

      if (response.success) {
        setShowCreateModal(false);
        // Reset form
        setFormData({
          username: "",
          email: "",
          password: "",
          role: "",
          countries: [],
          permissions: [],
          profile: {
            firstName: "",
            lastName: "",
            phone: "",
            department: "",
          },
        });
        onRefresh();
        // loadLinkedAccounts removed - AlterCPA not used
      } else {
        setFormError(response.error || t("users.failedCreateUser"));
      }
    } catch (error: any) {
      console.error("Error creating user:", error);
      setFormError(error.message || t("users.failedCreateUser"));
    } finally {
      setCreating(false);
    }
  };

  const handleEditUser = async () => {
    if (!editingUser) return;

    setUpdating(true);
    setFormError(null);
    try {
      // Validate required fields
      if (!editFormData.username || !editFormData.email || !editFormData.role) {
        setFormError(t("users.formErrorEditRequired"));
        setUpdating(false);
        return;
      }

      if (!editFormData.profile.firstName || !editFormData.profile.lastName) {
        setFormError(t("users.formErrorNameRequired"));
        setUpdating(false);
        return;
      }

      if (editFormData.countries.length === 0) {
        setFormError(t("users.formErrorCountryRequired"));
        setUpdating(false);
        return;
      }

      if (editFormData.permissions.length === 0) {
        setFormError(t("users.formErrorPermissionRequired"));
        setUpdating(false);
        return;
      }

      // Prepare update data
      const updateData = {
        username: editFormData.username.trim(),
        email: editFormData.email.trim(),
        role: editFormData.role,
        countries: editFormData.countries,
        permissions: editFormData.permissions,
        achievements: editFormData.achievements || [], // Include achievements
        // linkedAccountId removed for Serbia-CC
        isActive: editFormData.isActive,
        profile: {
          firstName: editFormData.profile.firstName.trim(),
          lastName: editFormData.profile.lastName.trim(),
          phone: editFormData.profile.phone.trim(),
          department: editFormData.profile.department.trim(),
        },
      };

      const response = await api.updateUser(editingUser.id, updateData);

      if (response.success) {
        setShowEditModal(false);
        setEditingUser(null);
        onRefresh();
        // loadLinkedAccounts removed - AlterCPA not used
      } else {
        setFormError(response.error || t("users.failedUpdateUser"));
      }
    } catch (error: any) {
      console.error("Error updating user:", error);
      setFormError(error.message || t("users.failedUpdateUser"));
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm(t("users.confirmDeleteUser"))) {
      try {
        const response = await api.deleteUser(userId);
        if (response.success) {
          onRefresh();
          // loadLinkedAccounts removed - AlterCPA not used
        } else {
          alert(response.error || t("users.failedDeleteUser"));
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        alert(t("users.failedDeleteUser"));
      }
    }
  };

  const handleToggleUserStatus = async (userId: string) => {
    try {
      const user = users.find((u) => u.id === userId);
      if (!user) return;

      const response = await api.updateUser(userId, {
        isActive: !user.isActive,
      });

      if (response.success) {
        onRefresh();
      } else {
        alert(response.error || t("users.failedToggleStatus"));
      }
    } catch (error) {
      console.error("Error toggling user status:", error);
      alert(t("users.failedToggleStatus"));
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      username: user.username,
      email: user.email,
      role: user.role,
      countries: user.countries,
      // linkedAccountId removed for Serbia-CC
      isActive: user.isActive,
      permissions: user.permissions || [],
      achievements: user.achievements || [], // Initialize achievements
      profile: { ...user.profile },
    });
    setShowEditModal(true);
    setFormError(null);
  };

  const handleCountryToggle = (
    countryCode: string,
    isCreate: boolean = true
  ) => {
    if (isCreate) {
      const newCountries = formData.countries.includes(countryCode)
        ? formData.countries.filter((c) => c !== countryCode)
        : [...formData.countries, countryCode];
      setFormData({ ...formData, countries: newCountries });
    } else {
      const newCountries = editFormData.countries.includes(countryCode)
        ? editFormData.countries.filter((c) => c !== countryCode)
        : [...editFormData.countries, countryCode];
      setEditFormData({ ...editFormData, countries: newCountries });
    }
  };

  const handlePermissionToggle = (
    permissionId: string,
    isCreate: boolean = true
  ) => {
    if (isCreate) {
      const newPermissions = formData.permissions.includes(permissionId)
        ? formData.permissions.filter((p) => p !== permissionId)
        : [...formData.permissions, permissionId];
      setFormData({ ...formData, permissions: newPermissions });
    } else {
      const newPermissions = editFormData.permissions.includes(permissionId)
        ? editFormData.permissions.filter((p) => p !== permissionId)
        : [...editFormData.permissions, permissionId];
      setEditFormData({ ...editFormData, permissions: newPermissions });
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.username.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      user.profile.firstName
        .toLowerCase()
        .includes(filters.searchTerm.toLowerCase()) ||
      user.profile.lastName
        .toLowerCase()
        .includes(filters.searchTerm.toLowerCase());

    const matchesRole =
      filters.selectedRole === "all" || user.role === filters.selectedRole;
    const matchesCountry =
      filters.selectedCountry === "all" ||
      user.countries.includes(filters.selectedCountry);
    const matchesActive = !filters.showActiveOnly || user.isActive;

    return matchesSearch && matchesRole && matchesCountry && matchesActive;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#264a3e]"></div>
        <span className="ml-2 text-gray-600">{t("users.loadingUsers")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            {t("users.management")}
          </h2>
          <p className="text-sm text-gray-600">
            {t("users.managementDescription")}
          </p>
        </div>
        <button
          onClick={() => {
            setShowCreateModal(true);
            setFormError(null);
            // Reset form data
            setFormData({
              username: "",
              email: "",
              password: "",
              role: "",
              countries: [],
              permissions: [],
              profile: {
                firstName: "",
                lastName: "",
                phone: "",
                department: "",
              },
            });
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t("users.addUser")}
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.search")}
            </label>
            <input
              type="text"
              placeholder={t("users.searchUsers")}
              value={filters.searchTerm}
              onChange={(e) =>
                onFilterChange({ ...filters, searchTerm: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.role")}
            </label>
            <select
              value={filters.selectedRole}
              onChange={(e) =>
                onFilterChange({ ...filters, selectedRole: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
            >
              <option value="all">{t("users.allRoles")}</option>
              {availableRoles.map((role) => (
                <option key={role} value={role}>
                  {role
                    .replace("-", " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("users.country")}
            </label>
            <select
              value={filters.selectedCountry}
              onChange={(e) =>
                onFilterChange({ ...filters, selectedCountry: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
            >
              <option value="all">{t("users.allCountries")}</option>
              {availableCountries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tableUser")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tableRole")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tableCountries")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tablePermissions")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tableStatus")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tableLastLogin")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("users.tableActions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => {
                  const RoleIcon = getRoleIcon(user.role);
                  const StatusIcon = getStatusIcon(user.isActive);

                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-[#264a3e] flex items-center justify-center">
                              <span className="text-sm font-medium text-white">
                                {user.profile.firstName.charAt(0)}
                                {user.profile.lastName.charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.profile.firstName} {user.profile.lastName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                            <div className="text-sm text-gray-500">
                              @{user.username}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <RoleIcon
                            className={`w-4 h-4 mr-2 ${getRoleColor(
                              user.role
                            )}`}
                          />
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBg(
                              user.role
                            )} ${getRoleColor(user.role)}`}
                          >
                            {user.role
                              .replace("-", " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {user.countries.slice(0, 2).map((country) => (
                            <span
                              key={country}
                              className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full"
                              title={COUNTRY_MAP[country] || country}
                            >
                              {country}
                            </span>
                          ))}
                          {user.countries.length > 2 && (
                            <span
                              className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full"
                              title={user.countries
                                .slice(2)
                                .map((c) => COUNTRY_MAP[c] || c)
                                .join(", ")}
                            >
                              +{user.countries.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {user.permissions && user.permissions.length > 0 ? (
                            <>
                              {user.permissions.slice(0, 3).map((permission) => (
                                <span
                                  key={permission}
                                  className="inline-flex px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-md"
                                  title={permission}
                                >
                                  {permission.split('.')[0]}
                                </span>
                              ))}
                              {user.permissions.length > 3 && (
                                <span
                                  className="inline-flex px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-md cursor-help"
                                  title={user.permissions.slice(3).join(", ")}
                                >
                                  +{user.permissions.length - 3}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">{t("users.noPermissions")}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <StatusIcon
                            className={`w-4 h-4 mr-2 ${getStatusColor(
                              user.isActive
                            )}`}
                          />
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBg(
                              user.isActive
                            )} ${getStatusColor(user.isActive)}`}
                          >
                            {user.isActive ? t("users.active") : t("users.inactive")}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.lastLogin ? formatDate(user.lastLogin) : t("users.never")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openEditModal(user)}
                            className="text-[#264a3e] hover:text-[#1e3a32]"
                            title={t("users.editUser")}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleUserStatus(user.id)}
                            className="text-gray-600 hover:text-gray-900"
                            title={user.isActive ? t("users.deactivate") : t("users.activate")}
                          >
                            {user.isActive ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t("users.deleteUser")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t("users.createNewUser")}
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {formError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                    <p className="text-sm text-red-800">{formError}</p>
                  </div>
                </div>
              )}

              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                {/* Account Information */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <h4 className="text-sm font-medium text-gray-900">
                    {t("users.accountInformation")}
                  </h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.username")} *
                      </label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) =>
                          setFormData({ ...formData, username: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.email")} *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("users.password")} *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) =>
                          setFormData({ ...formData, password: e.target.value })
                        }
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.role")} *
                      </label>
                      <select
                        value={formData.role}
                        onChange={(e) =>
                          setFormData({ ...formData, role: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      >
                        <option value="">{t("users.selectRole")}</option>
                        {availableRoles.map((role) => (
                          <option key={role} value={role}>
                            {role
                              .replace("-", " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* AlterCPA Account field removed for Serbia-CC */}
                  </div>
                </div>

                {/* Profile Information */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <h4 className="text-sm font-medium text-gray-900">
                    {t("users.profileInformation")}
                  </h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.firstName")} *
                      </label>
                      <input
                        type="text"
                        value={formData.profile.firstName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            profile: {
                              ...formData.profile,
                              firstName: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.lastName")} *
                      </label>
                      <input
                        type="text"
                        value={formData.profile.lastName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            profile: {
                              ...formData.profile,
                              lastName: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.phone")}
                      </label>
                      <input
                        type="text"
                        value={formData.profile.phone}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            profile: {
                              ...formData.profile,
                              phone: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.department")}
                      </label>
                      <input
                        type="text"
                        value={formData.profile.department}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            profile: {
                              ...formData.profile,
                              department: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Countries - Hidden for website-post-manager */}
                {formData.role !== "website-post-manager" && (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                    <h4 className="text-sm font-medium text-gray-900">
                      Countries *
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {availableCountries.map((country) => (
                        <label
                          key={country.code}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.countries.includes(country.code)}
                            onChange={() =>
                              handleCountryToggle(country.code, true)
                            }
                            className="rounded border-gray-300 text-[#264a3e] focus:ring-[#264a3e]"
                          />
                          <span className="text-sm text-gray-700">
                            {country.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Auto-configured info for website-post-manager */}
                {formData.role === "website-post-manager" && (
                  <div className="bg-teal-50 border border-teal-200 p-4 rounded-lg space-y-2">
                    <h4 className="text-sm font-medium text-teal-900">
                      Auto-configured Settings
                    </h4>
                    <p className="text-sm text-teal-700">
                      This role is automatically configured with:
                    </p>
                    <ul className="list-disc list-inside text-sm text-teal-700 space-y-1">
                      <li><strong>Country:</strong> Greece (GR)</li>
                      <li><strong>Permissions:</strong> View Shipping only (full CRUD access)</li>
                      <li><strong>Source:</strong> Handles only bulk import orders with source_tag: website</li>
                      <li><strong>Courier:</strong> ACS Post Office only</li>
                    </ul>
                  </div>
                )}

                {/* Permissions - Hidden for website-post-manager */}
                {formData.role !== "website-post-manager" && (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900">
                        {t("users.permissions")} *
                      </h4>
                      <span className="text-xs text-gray-500">
                        {formData.permissions.length} selected
                      </span>
                    </div>
                    <div className="space-y-4">
                      {availablePermissions.map((group) => (
                        <div key={group.category}>
                          <h5 className="text-xs font-semibold text-gray-700 uppercase mb-2">
                            {group.category}
                          </h5>
                          <div className="grid grid-cols-2 gap-2 pl-2">
                            {group.permissions.map((permission) => (
                              <label
                                key={permission.id}
                                className="flex items-center space-x-2 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.permissions.includes(
                                    permission.id
                                  )}
                                  onChange={() =>
                                    handlePermissionToggle(permission.id, true)
                                  }
                                  className="rounded border-gray-300 text-[#264a3e] focus:ring-[#264a3e]"
                                />
                                <span className="text-sm text-gray-700">
                                  {permission.name}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
                >
                  {t("users.cancel")}
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={creating}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#264a3e] border border-transparent rounded-md hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] disabled:opacity-50"
                >
                  {creating ? t("users.creating") : t("users.addUser")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t("users.editUserTitle")}</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {formError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                    <p className="text-sm text-red-800">{formError}</p>
                  </div>
                </div>
              )}

              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                {/* Account Information */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <h4 className="text-sm font-medium text-gray-900">
                    {t("users.accountInformation")}
                  </h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.username")} *
                      </label>
                      <input
                        type="text"
                        value={editFormData.username}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            username: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            email: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role *
                      </label>
                      <select
                        value={editFormData.role}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            role: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      >
                        {availableRoles.map((role) => (
                          <option key={role} value={role}>
                            {role
                              .replace("-", " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* AlterCPA Account field removed for Serbia-CC */}
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editFormData.isActive}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          isActive: e.target.checked,
                        })
                      }
                      className="rounded border-gray-300 text-[#264a3e] focus:ring-[#264a3e]"
                    />
                    <label className="ml-2 text-sm text-gray-700">
                      Active Account
                    </label>
                  </div>
                </div>

                {/* Profile Information */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <h4 className="text-sm font-medium text-gray-900">
                    {t("users.profileInformation")}
                  </h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.firstName")} *
                      </label>
                      <input
                        type="text"
                        value={editFormData.profile.firstName}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            profile: {
                              ...editFormData.profile,
                              firstName: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.lastName")} *
                      </label>
                      <input
                        type="text"
                        value={editFormData.profile.lastName}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            profile: {
                              ...editFormData.profile,
                              lastName: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.phone")}
                      </label>
                      <input
                        type="text"
                        value={editFormData.profile.phone}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            profile: {
                              ...editFormData.profile,
                              phone: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("users.department")}
                      </label>
                      <input
                        type="text"
                        value={editFormData.profile.department}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            profile: {
                              ...editFormData.profile,
                              department: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Countries */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <h4 className="text-sm font-medium text-gray-900">
                    {t("users.countries")} *
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {availableCountries.map((country) => (
                      <label
                        key={country.code}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={editFormData.countries.includes(
                            country.code
                          )}
                          onChange={() =>
                            handleCountryToggle(country.code, false)
                          }
                          className="rounded border-gray-300 text-[#264a3e] focus:ring-[#264a3e]"
                        />
                        <span className="text-sm text-gray-700">
                          {country.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Permissions */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">
                      {t("users.permissions")} *
                    </h4>
                    <span className="text-xs text-gray-500">
                      {editFormData.permissions.length} selected
                    </span>
                  </div>
                  <div className="space-y-4">
                    {availablePermissions.map((group) => (
                      <div key={group.category}>
                        <h5 className="text-xs font-semibold text-gray-700 uppercase mb-2">
                          {group.category}
                        </h5>
                        <div className="grid grid-cols-2 gap-2 pl-2">
                          {group.permissions.map((permission) => (
                            <label
                              key={permission.id}
                              className="flex items-center space-x-2 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={editFormData.permissions.includes(
                                  permission.id
                                )}
                                onChange={() =>
                                  handlePermissionToggle(permission.id, false)
                                }
                                className="rounded border-gray-300 text-[#264a3e] focus:ring-[#264a3e]"
                              />
                              <span className="text-sm text-gray-700">
                                {permission.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Task Achievements (for call operators) */}
                {editFormData.role === 'call-operator' && (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900">
                        Task Achievements (Badges)
                      </h4>
                      <span className="text-xs text-gray-500">
                        {editFormData.achievements.length} achieved
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      Select tasks this operator has achieved. Operators can only take leads for tasks they have achieved.
                    </p>
                    <div className="space-y-2">
                      {[
                        { id: 'update_address', name: 'Update Address', color: 'bg-blue-100 text-blue-800 border-blue-200' },
                        { id: 'sell_new_product', name: 'Try to Sell New Product', color: 'bg-green-100 text-green-800 border-green-200' },
                        { id: 'collect_more_data', name: 'Collect More Data', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
                        { id: 'new_dose', name: 'Resell', color: 'bg-purple-100 text-purple-800 border-purple-200' },
                      ].map((task) => (
                        <label
                          key={task.id}
                          className="flex items-center space-x-3 cursor-pointer p-2 rounded-md hover:bg-gray-100 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={editFormData.achievements.includes(task.id)}
                            onChange={() => {
                              const newAchievements = editFormData.achievements.includes(task.id)
                                ? editFormData.achievements.filter((a) => a !== task.id)
                                : [...editFormData.achievements, task.id];
                              setEditFormData({ ...editFormData, achievements: newAchievements });
                            }}
                            className="rounded border-gray-300 text-[#264a3e] focus:ring-[#264a3e]"
                          />
                          <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border ${task.color}`}>
                            {task.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
                >
                  {t("users.cancel")}
                </button>
                <button
                  onClick={handleEditUser}
                  disabled={updating}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#264a3e] border border-transparent rounded-md hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] disabled:opacity-50"
                >
                  {updating ? t("users.updating") : t("users.updateUser")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersTab;
